import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import useNavigationService from 'hooks/useNavigationService';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { RootState } from 'redux/store';
import { isUsuarioSomenteLeitura } from 'services/usuario';
import { formatCnpjCpf } from 'utils/formatUtils';
import { alertDefaultConfig } from 'utils/toastifyUtils';
import { Paginador, parseEtapaParalisacaoDescricao } from 'utils/types';
import { IArquivoEditavel } from 'utils/types/arquivoEditavel';
import { ObrigacaoDescricao } from 'utils/types/obrigacao';

import { Cached, EditOutlined, LockOutlined } from '@mui/icons-material';
import {
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import AlertModal from 'components/AlertModal/AlertModal';

import {
  useReprocessarArquivo,
  useSolicitarEdicao,
} from './arquivosEditaveis.service';

interface DataTableArquivosEditaveisProps {
  data: Paginador<IArquivoEditavel> | [];
  page: number;
  rowsPerPage: number;
  setPage: (value: number) => void;
  setRowsPerPage: (value: number) => void;
}

const DataTableArquivosEditaveis = (props: DataTableArquivosEditaveisProps) => {
  const queryClient = useQueryClient();

  const { navegarParaEdicao } = useNavigationService();

  const { id: usuarioID, perfilIDs: perfisIDsUsuario } = useSelector(
    (state: RootState) => state.token.usuario,
  );

  const solicitarEdicaoMutation = useSolicitarEdicao(queryClient);
  const reprocessarArquivoMutation = useReprocessarArquivo(queryClient);

  const solicitarEdicao = async (
    usuarioID: number,
    processamentoID: number,
  ) => {
    try {
      if (isUsuarioSomenteLeitura(perfisIDsUsuario)) {
        toast.info(
          'Operação não permitida para o seu perfil de usuário.',
          alertDefaultConfig,
        );
        return;
      }
      await solicitarEdicaoMutation.mutateAsync({ usuarioID, processamentoID });
      navegarParaEdicao(processamentoID);
    } catch (error: any) {
      let mensagem =
        'Erro ao solicitar edição. Outro usuário pode estar editando o arquivo no momento.';
      if (error?.response?.status === 404) {
        mensagem = 'Você não tem permissão para editar este arquivo.';
      }
      toast.error(mensagem);
    }
  };

  const isPaginador = (
    data: Paginador<IArquivoEditavel> | [],
  ): data is Paginador<IArquivoEditavel> => {
    return (data as Paginador<IArquivoEditavel>).conteudo !== undefined;
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    props.setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    props.setRowsPerPage(parseInt(event.target.value, 10));
    props.setPage(1);
  };

  const [modalReprocessar, setModalReprocessar] = useState<{
    open: boolean;
    row: IArquivoEditavel | undefined;
  }>({ open: false, row: undefined });

  const handleReprocessarModal = (row?: IArquivoEditavel) =>
    setModalReprocessar({ open: !modalReprocessar.open, row });

  const reprocessarArquivo = async (processamentoID: number) => {
    try {
      if (isUsuarioSomenteLeitura(perfisIDsUsuario)) {
        toast.info(
          'Operação não permitida para o seu perfil de usuário.',
          alertDefaultConfig,
        );
        return;
      }
      await reprocessarArquivoMutation.mutateAsync(processamentoID);
      toast.success('Reprocessamento iniciado com sucesso.');
    } catch (error) {
      console.error('Erro ao solicitar o reprocessamento do arquivo:', error);
      toast.error('Erro ao solicitar o reprocessamento do arquivo.');
    }
    handleReprocessarModal();
  };

  return (
    <>
      <AlertModal
        open={modalReprocessar.open}
        onClose={handleReprocessarModal}
        confirmButtonAction={async () =>
          await reprocessarArquivo(modalReprocessar.row!.processamentoSpedID)
        }
        title="Atenção"
        content="Ao reprocessar este arquivo:
        <ul>
        <li>Um novo arquivo fiscal será gerado.</li>
        <li>O arquivo editado ficará indisponível para nova edição.</li>
        <li>Será iniciado um novo processamento para esta empresa, obrigação e competência.</li>
        </ul>"
        additionalInfo="Deseja continuar?"
        cancelButtonText="Não"
        confirmButtonText="Sim"
      />
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Obrigação</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Etapa</TableCell>
              <TableCell>Data de Processamento</TableCell>
              <TableCell>CPF/CNPJ</TableCell>
              <TableCell>Nome Empresarial</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isPaginador(props.data) &&
              props.data.conteudo.map(
                (row: IArquivoEditavel, index: number) => (
                  <>
                    <TableRow
                      key={index}
                      sx={{
                        backgroundColor:
                          index % 2 === 0
                            ? 'var(--mui-palette-background-paper)'
                            : 'var(--mui-palette-background-default)',
                        color: row.gerandoArquivoEditado
                          ? 'primary'
                          : !row.podeEditar
                            ? 'darkgray'
                            : 'GrayText',
                      }}
                    >
                      <TableCell
                        sx={{
                          color: row.gerandoArquivoEditado
                            ? 'primary'
                            : !row.podeEditar
                              ? 'darkgray'
                              : 'GrayText',
                        }}
                      >
                        {ObrigacaoDescricao[row.obrigacaoID]}
                        <br />
                        {dayjs(row.competencia).format('MM/YYYY')}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: row.gerandoArquivoEditado
                            ? 'primary'
                            : !row.podeEditar
                              ? 'darkgray'
                              : 'GrayText',
                        }}
                      >
                        {row.tipoArquivo}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: row.gerandoArquivoEditado
                            ? 'primary'
                            : !row.podeEditar
                              ? 'darkgray'
                              : 'GrayText',
                        }}
                      >
                        {parseEtapaParalisacaoDescricao(row.etapaParalisacao)}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: row.gerandoArquivoEditado
                            ? 'primary'
                            : !row.podeEditar
                              ? 'darkgray'
                              : 'GrayText',
                        }}
                      >
                        {dayjs(row.dataProcessamento).format(
                          'DD/MM/YYYY HH:mm',
                        )}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: row.gerandoArquivoEditado
                            ? 'primary'
                            : !row.podeEditar
                              ? 'darkgray'
                              : 'GrayText',
                        }}
                      >
                        {formatCnpjCpf(row.empresaClienteNumeroRegistro)}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: row.gerandoArquivoEditado
                            ? 'primary'
                            : !row.podeEditar
                              ? 'darkgray'
                              : 'GrayText',
                        }}
                      >
                        {row.empresaClienteNomeEmpresarial}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: row.gerandoArquivoEditado
                            ? 'primary'
                            : !row.podeEditar
                              ? 'darkgray'
                              : 'GrayText',
                        }}
                      >
                        {row.podeEditar && !row.gerandoArquivoEditado && (
                          <Tooltip title="Editar Arquivo">
                            <IconButton
                              data-testid="botao-editar"
                              onClick={() =>
                                solicitarEdicao(
                                  usuarioID,
                                  row.processamentoSpedID,
                                )
                              }
                            >
                              <EditOutlined color="primary" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {!row.podeEditar && !row.gerandoArquivoEditado && (
                          <Tooltip title="Arquivo bloqueado. Outro usuário está editando no momento.">
                            <IconButton>
                              <LockOutlined color="error" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {row.gerandoArquivoEditado && (
                          <CircularProgress size={25} />
                        )}
                        {row.podeReprocessar && (
                          <Tooltip title="Iniciar reprocessamento do arquivo">
                            <IconButton
                              data-testid="botao-reprocessar"
                              onClick={() => handleReprocessarModal(row)}
                            >
                              <Cached color="success" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  </>
                ),
              )}
          </TableBody>
        </Table>
      </TableContainer>
      {isPaginador(props.data) && props.data.conteudo.length === 0 && (
        <Typography
          component="h6"
          variant="h6"
          color="text.primary"
          align="center"
          sx={{
            marginTop: '2em',
            marginBottom: '2em',
            fontWeight: 400,
            fontSize: '1.2em',
            color: 'var(--mui-palette-text-primary)',
          }}
        >
          Não há arquivos disponíveis para edição.
        </Typography>
      )}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={isPaginador(props.data) ? props.data.totalConteudo : 0}
        rowsPerPage={props.rowsPerPage}
        page={props.page - 1}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Itens por página"
        labelDisplayedRows={({ from, to, count }) =>
          `${from} - ${to} de ${count !== -1 ? count : `mais de ${to}`}`
        }
      />
    </>
  );
};

export default DataTableArquivosEditaveis;
